import type { ReactNode } from 'react';
import { Panel } from '../ui/Panel';
import { Field } from '../ui/Field';
import { NumberInput } from '../ui/NumberInput';
import { LoadFailed } from '../ui/LoadState';
import { Skeleton } from '../../ui/Skeleton';
import { btnPrimary, btnSecondary } from '../ui/styles';
import { useCan } from '../useCan';
import { useCostsDraft } from '../margin/useCosts';
import { MarginLines } from '../margin/MarginLines';
import { boxContribution, minBoxFor, orderContribution } from '../../core/rules/margin';
import { euro, money, toCents } from '../../core/format/currency';
import { dayShort, today } from '../../core/format/dates';
import { exVat } from '../../core/rules/revenue';
import type { CostModel, FeeKey, ShipKind } from '../../core/types/margin';
const FEES: {
  key: FeeKey;
  label: string;
  hint: string;
}[] = [{
  key: 'ideal',
  label: 'iDEAL / Wero',
  hint: 'Mollie, per betaling'
}, {
  key: 'bancontact',
  label: 'Bancontact',
  hint: 'Mollie, België'
}, {
  key: 'card',
  label: 'Creditcard',
  hint: 'Mollie'
}, {
  key: 'klarna',
  label: 'Klarna',
  hint: 'Achteraf betalen'
}, {
  key: 'incasso',
  label: 'Incasso (bijvuldoos)',
  hint: 'Ook bij een mislukte incasso'
}, {
  key: 'pin',
  label: 'Pinnen in de salon',
  hint: 'Pinautomaat; contant kost niets'
}];
const SHIPS: {
  key: ShipKind;
  label: string;
}[] = [{
  key: 'brievenbus-nl',
  label: 'Brievenbuspakje NL'
}, {
  key: 'pakket-nl',
  label: 'Pakket NL'
}, {
  key: 'pakket-be',
  label: 'Pakket België'
}];

/** Een gemiddeld voorbeeld: serum van € 56 met 40% brutomarge. */
const EXAMPLE_GROSS = 5600;
const EXAMPLE_MARGIN = 40;
const cents = (euros: number | undefined) => toCents(euros ?? 0);

/**
 * Instellingen › Kosten en marge. Alles zonder btw en ingeschat tot de echte Mollie- en
 * PostNL-cijfers er zijn. Sarah's eigen tijd telt bewust niet mee. Opslaan gebeurt hier zelf
 * (eigen knop), niet via de Opslaan-balk: het staat los van de site-instellingen en is nooit publiek.
 */
export function CostSettings() {
  const {
    canAll
  } = useCan();
  // Instellingen toont dit onderwerp alleen met finance.margin; hier nog eens, zodat het nooit 403 vraagt.
  return canAll('finance.margin') ? <CostSettingsForm canEdit={canAll('settings.edit')} data-id="element-4165" data-mp="3cneu6r0duulb" /> : null;
}
function CostSettingsForm({
  canEdit
}: {
  canEdit: boolean;
}) {
  const draft = useCostsDraft();
  const c = draft.current;
  if (draft.status === 'error' && !c) return <LoadFailed title="De kosten konden niet laden" onRetry={draft.reload} data-id="element-4166" data-mp="2orsl2voghqnn" />;
  if (!c) return <Skeleton className="h-64 w-full rounded-lg" data-id="element-4167" data-mp="2tqnf6trvj1n2" />;
  const edit = (fn: (m: CostModel) => CostModel) => draft.edit(fn);
  const exampleNet = exVat(EXAMPLE_GROSS, 21);
  const example = orderContribution({
    kind: 'los',
    lines: [{
      name: 'Serum',
      qty: 1,
      grossCents: EXAMPLE_GROSS,
      vatRate: 21,
      costCents: Math.round(exampleNet * (1 - EXAMPLE_MARGIN / 100))
    }],
    paymentMethod: 'ideal',
    paidCents: EXAMPLE_GROSS,
    shipping: 'pakket-nl'
  }, c);
  const advice = minBoxFor(c.targetBoxLeftCents, EXAMPLE_MARGIN, c);
  return <>
      <Panel title="Zo rekent het nu" description="Een losse bestelling van € 56 met gratis verzending, bij 40% brutomarge. Rekent mee terwijl je typt." data-id="element-4168" data-mp="23fto4tkrwezd">
        <MarginLines breakdown={example} className="max-w-md" data-id="element-4169" data-mp="1tq1sdw0xtftb" />
        <p className="mt-4 text-[12px] leading-relaxed text-muted" data-id="element-4170" data-mp="1togxu6emhwvg">
          “Wat er overblijft” is de bijdrage aan je vaste lasten en aan jou, geen nettowinst. Huur, software en je eigen tijd tellen niet mee. Alle bedragen zonder btw, ingeschat · laatst
          nagelopen {dayShort(c.checkedOn)}.
        </p>
      </Panel>

      <fieldset disabled={!canEdit || draft.saving} className="space-y-6 disabled:opacity-90" data-id="element-4171" data-mp="1arfroi9c1qpx">
        <legend className="sr-only" data-id="element-4172" data-mp="15te87w7zvpdx">Kosten</legend>
        {!canEdit && <p className="text-[13px] text-muted" data-id="element-4173" data-mp="15uz53shs7key">Je kunt de kosten bekijken; aanpassen kan alleen wie de instellingen beheert.</p>}

        <Panel title="Betalen" description="Vast bedrag plus een percentage van het bedrag, per betaling." data-id="element-4174" data-mp="3075garaoe179">
          <ul className="divide-y divide-line" data-id="element-4175" data-mp="1xsy5hrhu0doa">
            {FEES.map(f => <li key={f.key} className="grid gap-2 py-3 first:pt-0 last:pb-0 sm:grid-cols-[minmax(0,1fr)_8rem_7rem] sm:items-center sm:gap-4" data-id="element-4176" data-mp="21v2x6kopz08a">
                <span className="min-w-0" data-id="element-4177" data-mp="1nuic1hvc0sbm">
                  <span className="block text-[14px] text-ink" data-id="element-4178" data-mp="1suefng04ahqn">{f.label}</span>
                  <span className="block text-[12px] text-muted" data-id="element-4179" data-mp="1swhfssuzhr93">{f.hint}</span>
                </span>
                <Labeled label={`${f.label}: vast bedrag in euro`} suffix="€" data-id="element-4180" data-mp="1e3dvwetx71f7">
                  <NumberInput decimals value={c.payment[f.key].fixedCents / 100} onChange={v => edit(m => ({
                ...m,
                payment: {
                  ...m.payment,
                  [f.key]: {
                    ...m.payment[f.key],
                    fixedCents: cents(v)
                  }
                }
              }))} max={5} data-id="element-4181" data-mp="1ixrueq3vpoo3" />
                </Labeled>
                <Labeled label={`${f.label}: percentage`} suffix="%" data-id="element-4182" data-mp="v6meyc1uoyeb">
                  <NumberInput decimals value={c.payment[f.key].percent} onChange={v => edit(m => ({
                ...m,
                payment: {
                  ...m.payment,
                  [f.key]: {
                    ...m.payment[f.key],
                    percent: v ?? 0
                  }
                }
              }))} max={20} data-id="element-4183" data-mp="v7ea5lrjcu42" />
                </Labeled>
              </li>)}
          </ul>
        </Panel>

        <Panel title="Verzenden en verpakking" description="Wat jij betaalt per zending, ook als de klant gratis verzending heeft." data-id="element-4184" data-mp="ck56vxi7gf2k">
          <div className="grid gap-5 sm:grid-cols-3" data-id="element-4185" data-mp="lj0e9cwzjv7g">
            {SHIPS.map(s => <Field key={s.key} label={`${s.label} (€)`} data-id="element-4186" data-mp="lld4edcwdbvl">
                <NumberInput decimals value={c.shipping[s.key] / 100} onChange={v => edit(m => ({
              ...m,
              shipping: {
                ...m.shipping,
                [s.key]: cents(v)
              }
            }))} max={50} data-id="element-4187" data-mp="zhgtjs8wyu17" />
              </Field>)}
            <Field label="Verpakking pakket (€)" hint="Doos, vulling, sticker en kaartje." data-id="element-4188" data-mp="19836fep8x6g7">
              <NumberInput decimals value={c.packaging.pakket / 100} onChange={v => edit(m => ({
              ...m,
              packaging: {
                ...m.packaging,
                pakket: cents(v)
              }
            }))} max={20} data-id="element-4189" data-mp="14at9zf3fvuiv" />
            </Field>
            <Field label="Verpakking brievenbus (€)" data-id="element-4190" data-mp="1e8qxg0dh5naf">
              <NumberInput decimals value={c.packaging.brievenbus / 100} onChange={v => edit(m => ({
              ...m,
              packaging: {
                ...m.packaging,
                brievenbus: cents(v)
              }
            }))} max={20} data-id="element-4191" data-mp="1law2uf4256yf" />
            </Field>
          </div>
        </Panel>

        <Panel title="Retouren en bijvullen" data-id="element-4192" data-mp="1v8s84qabd7om">
          <div className="grid gap-5 sm:grid-cols-3" data-id="element-4193" data-mp="1v6z139vsglg3">
            <Field label="Retourvoorziening (%)" hint="Van de productomzet in de webshop." data-id="element-4194" data-mp="20021a0v2mt59">
              <NumberInput decimals value={c.returnsPercent} onChange={v => edit(m => ({
              ...m,
              returnsPercent: v ?? 0
            }))} max={50} data-id="element-4195" data-mp="24zmxi3iuwnea" />
            </Field>
            <Field label="Per bijvuldoos minstens over (€)" hint="Voor het advies bij het minimumbedrag." data-id="element-4196" data-mp="292b7wfn7y2dd">
              <NumberInput decimals value={c.targetBoxLeftCents / 100} onChange={v => edit(m => ({
              ...m,
              targetBoxLeftCents: cents(v)
            }))} max={200} data-id="element-4197" data-mp="2iymlra2gj09x" />
            </Field>
            <Field label="Abonnementscadeau kost (€)" hint="€ 0 als het gratis van de leverancier komt (try-me sizes)." data-id="element-4198" data-mp="jnd20gudjv4w">
              <NumberInput decimals value={c.subscriptionGiftCents / 100} onChange={v => edit(m => ({
              ...m,
              subscriptionGiftCents: cents(v)
            }))} max={100} data-id="element-4199" data-mp="omg7g2e9o3de" />
            </Field>
          </div>
          <p className="mt-4 text-[13px] text-muted" data-id="element-4200" data-mp="c8j805v3z27n">
            {advice === null ? 'Bij 40% brutomarge dekt een bijvuldoos deze kosten niet.' : `Bij 40% brutomarge laat een doos van € 75 ${money(boxContribution(7500, EXAMPLE_MARGIN, c))} over; voor ${euro(c.targetBoxLeftCents / 100, {
            cents: c.targetBoxLeftCents % 100 !== 0
          })} per doos is het minimum ± ${euro(advice)}.`}
          </p>
        </Panel>
      </fieldset>

      {canEdit && <div className="sticky bottom-4 z-sticky flex flex-wrap items-center justify-end gap-3 rounded-lg border border-line bg-white px-4 py-3 shadow-sm" data-id="element-4201" data-mp="q07nmj4kg8td">
          <span className="mr-auto text-[13px] text-muted" data-id="element-4202" data-mp="3asj7ao0rzw0l">{draft.dirty ? 'Niet opgeslagen wijzigingen' : `Laatst nagelopen ${dayShort(c.checkedOn)}`}</span>
          {draft.dirty ? <button type="button" onClick={draft.discard} disabled={draft.saving} className={btnSecondary} data-id="element-4203" data-mp="3vhbz6yk7y1aq">
              Verwerpen
            </button> : <button type="button" onClick={() => edit(m => ({
        ...m,
        checkedOn: today()
      }))} className={btnSecondary} data-id="element-4204" data-mp="48obibyia200">
              Alles klopt, vandaag nagelopen
            </button>}
          <button type="button" onClick={() => void draft.save()} disabled={!draft.dirty || draft.saving} className={btnPrimary} data-id="element-4205" data-mp="3qk20esaxwda8">
            {draft.saving ? 'Opslaan…' : 'Kosten opslaan'}
          </button>
        </div>}
    </>;
}

/** Een veld zonder zichtbaar label (de rij zegt al wat het is), met eenheid erachter. */
function Labeled({
  label,
  suffix,
  children
}: {
  label: string;
  suffix: string;
  children: ReactNode;
}) {
  return <label className="flex items-center gap-2" data-id="element-4206" data-mp="110i9ny0vhgt0">
      <span className="sr-only" data-id="element-4207" data-mp="10zqj51m4u99l">{label}</span>
      <span className="min-w-0 flex-1" data-id="element-4208" data-mp="15sa0m9nxxojo">{children}</span>
      <span aria-hidden="true" className="w-3 text-[13px] text-muted" data-id="element-4209" data-mp="15tutluulwzff">
        {suffix}
      </span>
    </label>;
}